import { List, Record } from "immutable";
import { DocumentRecord, IDocumentData, documentFromJSON as docFromJSON } from "common/records/Document";
import { DateStringNumber, Nullable } from "common/records";
import { PriceListState, initialState as PriceListInitialState } from "common/reducers/priceList.reducer";
import {
  ProductOptionGroupState,
  initialState as ProductOptionGroupInitialState,
} from "common/reducers/productOptionGroup.reducer";
import { ProductOptionState, initialState as ProductOptionInitialState } from "common/reducers/productOption.reducer";
import { checkDate } from "common/helpers/Date";
import { IProductData } from "common/records/Product";
import { IProductOptionGroupData } from "common/records/ProductOptionGroup";

export const activatedPriceListFromJSON = (json: Partial<IActivatedPriceListData>): ActivatedPriceListRecord => {
  const recordData: IActivatedPriceListRecord = { ...(json as any) };

  if (json.file) {
    recordData.file = docFromJSON(json.file);
  }
  if (json.csv_generated_on) {
    recordData.csv_generated_on = checkDate(json.csv_generated_on);
  }
  if (json.created_at) {
    recordData.created_at = checkDate(json.created_at);
  }
  if (json.updated_at) {
    recordData.updated_at = checkDate(json.updated_at);
  }
  // A `products` key (even `[]`) marks a CACHED snapshot vs a SUMMARY record (org/list payload) that
  // shares the id but carries no products. `cached` lets consumers treat an empty snapshot as hydrated.
  if (json.products) {
    recordData.cached = true;
    // Roots only; the reducer fans descendants/options/links into the global stores.
    recordData.product_ids = List(json.products.map((product: any) => product.id));
  }

  return new ActivatedPriceListRecord(recordData);
};

/**
 * @deprecated Use the named export `activatedPriceListFromJSON`. Retained as a thin alias so
 * existing importers (`import { fromJSON } from "common/records/ActivatedPriceList"`) keep
 * working; new code should import `activatedPriceListFromJSON` directly.
 */
export const fromJSON = activatedPriceListFromJSON;

export interface IActivatedPriceListData {
  id: number;
  org_id: number;
  name: string;
  price_list_id: number;
  file?: IDocumentData;
  hidden_products: boolean;
  csv_url: string;
  csv_generated_on: DateStringNumber;
  location: string;
  estimate_count: number;
  activated_price_list_id: number;
  loading: boolean;
  products?: IProductData[];
  product_option_groups?: IProductOptionGroupData[];

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IActivatedPriceListRecord {
  id: number;
  org_id: number;
  name: string;
  price_list_id: number;
  priceLists: PriceListState;
  product_ids: List<number>;
  // True once the CACHED snapshot loaded (payload had `products`); false on SUMMARY records.
  cached: boolean;
  productOptionGroups: ProductOptionGroupState;
  productOptions: ProductOptionState;
  file: Nullable<DocumentRecord>;
  hidden_products: boolean;
  csv_url: string;
  csv_generated_on: Nullable<Date>;
  location: string;
  estimate_count: number;
  activated_price_list_id: Nullable<number>;
  loading: boolean;

  created_at: Date;
  updated_at: Date;
}

export const defaultActivatedPriceListProps = {
  id: 0,
  org_id: 0,
  name: "",
  price_list_id: 0,
  priceLists: PriceListInitialState,
  product_ids: List<number>(),
  cached: false,
  productOptionGroups: ProductOptionGroupInitialState,
  productOptions: ProductOptionInitialState,
  file: null,
  hidden_products: false,
  csv_url: "",
  csv_generated_on: null,
  location: "",
  activated_price_list_id: null,
  loading: false,
  estimate_count: 0,
  created_at: new Date(),
  updated_at: new Date(),
};

export class ActivatedPriceListRecord
  extends Record<IActivatedPriceListRecord>(defaultActivatedPriceListProps)
  implements IActivatedPriceListRecord {}
